import type { MedplumClient } from '@medplum/core';
import type { Appointment, Patient } from '@medplum/fhirtypes';
import {
  applyClientLocationConfirmationToAppointment,
  buildClientLocationConfirmation,
  getClientLocationConfirmationForPatient,
  getClientLocationConfirmationsFromAppointment,
  hasValidClientLocationConfirmation,
  patientHomeAddressPrefill,
} from './clientLocationConfirmationFhir';
import type {
  ClientLocationConfirmation,
  LocationConfirmationInput,
  LocationConfirmationResult,
} from './types';

export {
  getClientLocationConfirmationForPatient,
  getClientLocationConfirmationsFromAppointment,
  hasValidClientLocationConfirmation,
  patientHomeAddressPrefill,
};

export async function fetchAppointmentLocationConfirmation(
  medplum: MedplumClient,
  appointmentId: string,
  patientId?: string,
): Promise<ClientLocationConfirmation | null> {
  const appointment = await medplum.readResource('Appointment', appointmentId);
  return patientId
    ? getClientLocationConfirmationForPatient(appointment, patientId)
    : getClientLocationConfirmationsFromAppointment(appointment)[0] ?? null;
}

export async function appointmentRequiresLocationConfirmation(
  medplum: MedplumClient,
  appointmentId: string,
  patientId?: string,
): Promise<{ required: boolean; appointment: Appointment; existing: ClientLocationConfirmation | null }> {
  const appointment = await medplum.readResource('Appointment', appointmentId);
  const existing = patientId
    ? getClientLocationConfirmationForPatient(appointment, patientId)
    : null;
  const validForPatient = patientId ? existing != null : hasValidClientLocationConfirmation(appointment);
  return {
    required: !validForPatient,
    appointment,
    existing: validForPatient ? existing : null,
  };
}

export async function saveClientLocationConfirmation(
  medplum: MedplumClient,
  opts: {
    appointment: Appointment;
    patientId: string;
    input: LocationConfirmationInput;
    /** When true, overwrite an existing confirmation (provider edit). */
    force?: boolean;
  },
): Promise<LocationConfirmationResult> {
  if (!opts.force && hasValidClientLocationConfirmation(opts.appointment, opts.patientId)) {
    const existing = getClientLocationConfirmationForPatient(opts.appointment, opts.patientId);
    if (existing) {
      return { confirmation: existing };
    }
  }

  const confirmation = buildClientLocationConfirmation(opts.appointment, opts.patientId, opts.input, {
    sourceOverride: opts.force
      ? getClientLocationConfirmationForPatient(opts.appointment, opts.patientId)?.source
      : undefined,
  });
  const updated = applyClientLocationConfirmationToAppointment(opts.appointment, confirmation);
  const appointment = await medplum.updateResource(updated);
  medplum.invalidateSearches('Appointment');
  return { confirmation, appointment };
}

export async function loadPatientForAddressPrefill(
  medplum: MedplumClient,
  patientId: string,
): Promise<Patient | undefined> {
  try {
    return await medplum.readResource('Patient', patientId);
  } catch {
    return undefined;
  }
}
